import { createFileRoute, Link } from "@tanstack/react-router";
import { breadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { useServerFn } from "@tanstack/react-start";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { listPublishedPosts } from "@/lib/blog.functions";
import { ChevronRight } from "lucide-react";
import { responsiveImage } from "@/lib/responsive-image";

const SITE_URL = "https://elvion-group.nl";

const postsQO = queryOptions({
  queryKey: ["blog", "published"],
  queryFn: () => listPublishedPosts(),
});

export const Route = createFileRoute("/blog/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(postsQO),
  head: () => ({
    meta: [
      { title: "Blog over elektrotechniek — Elvion Group Nederland" },
      {
        name: "description",
        content:
          "Lees de laatste artikelen, tips en nieuws van Elvion Group Nederland over elektrotechniek, beveiliging, laadpalen en duurzame installaties.",
      },
      { property: "og:title", content: "Blog over elektrotechniek — Elvion Group Nederland" },
      {
        property: "og:description",
        content:
          "Artikelen, tips en nieuws over elektrotechniek en duurzame installaties.",
      },
      { property: "og:url", content: `${SITE_URL}/blog` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/blog` }],
    scripts: [breadcrumbJsonLd([{ name: "Blog", path: "/blog" }])],
  }),
  component: BlogIndex,
});

function fmtDate(s: string | null) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function BlogIndex() {
  const { data } = useSuspenseQuery(postsQO);
  const posts = data.posts;

  return (
    <>
      <Header />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <header className="mb-10">
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Blog — laatste nieuws &amp; weetjes van Elvion Groep
          </h1>
          <p className="mt-3 max-w-2xl text-text-secondary">
            Artikelen, tips en nieuws over elektrotechniek, beveiliging,
            laadpalen en duurzame installaties.
          </p>
        </header>

        {posts.length === 0 ? (
          <p className="rounded-lg border border-border bg-card p-8 text-center text-text-secondary">
            Er zijn nog geen artikelen gepubliceerd.
          </p>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => (
              <article
                key={p.id}
                className="overflow-hidden rounded-2xl bg-card shadow-sm ring-1 ring-border transition-shadow hover:shadow-md"
              >
                <Link
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="block"
                >
                  {p.cover_url ? (() => {
                    const r = responsiveImage(p.cover_url, [400, 640, 960]);
                    return (
                      <img
                        src={r?.src ?? p.cover_url}
                        srcSet={r?.srcSet}
                        sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                        alt={p.title}
                        loading="lazy"
                        decoding="async"
                        className="h-52 w-full object-cover"
                      />
                    );
                  })() : (
                    <div className="h-52 w-full bg-gradient-to-br from-primary/20 to-orange/20" />
                  )}
                </Link>
                <div className="p-5">
                  <span className="text-xs font-bold uppercase tracking-wider text-[var(--sky)]">
                    {fmtDate(p.published_at ?? p.created_at)}
                  </span>
                  <h2 className="mt-2 text-xl font-extrabold leading-snug text-foreground">
                    <Link
                      to="/blog/$slug"
                      params={{ slug: p.slug }}
                      className="hover:text-primary"
                    >
                      {p.title}
                    </Link>
                  </h2>
                  {p.excerpt && (
                    <p className="mt-2 line-clamp-3 text-sm text-text-secondary">
                      {p.excerpt}
                    </p>
                  )}
                  <Link
                    to="/blog/$slug"
                    params={{ slug: p.slug }}
                    aria-label={`Lees het volledige artikel: ${p.title}`}
                    className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[var(--sky)] hover:text-primary"
                  >
                    Lees het volledige artikel <ChevronRight className="size-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
